import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Lock, ShieldCheck, WifiOff } from "lucide-react";
import { useAuth, STAFF_ROLES } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [offline, setOffline] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setOffline(false);
    const res = await login(username, password);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      setOffline(res.status === 503);
      return;
    }
    navigate(STAFF_ROLES.includes(res.user.role) ? "/staff" : "/portal", { replace: true });
  };

  return (
    <div className="app-shell grid lg:grid-cols-[1.05fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1687289133469-b2a07a13b78b?auto=format&fit=crop&w=1400&q=70"
          alt="Scales of justice"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy/85 to-navy/55" />
        <div className="relative z-10 flex h-full flex-col justify-between p-16">
          <div>
            <p className="font-display text-2xl tracking-[0.32em] text-gold">VINEFORD</p>
            <p className="mt-2 text-xs uppercase tracking-[0.3em] text-[#9CA3AF]">Investigations · Since 1998</p>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl sm:text-5xl leading-tight text-[#F3F4F6]">
              Your matter, <span className="text-gold">documented</span> end to end.
            </h1>
            <p className="mt-6 text-base text-[#9CA3AF] leading-relaxed">
              A single secure gateway to your matter — progress, evidence, invoices and direct contact with your case
              team.
            </p>
            <div className="mt-10 flex items-center gap-2 text-xs text-[#9CA3AF]">
              <ShieldCheck className="h-4 w-4 text-gold" /> Encrypted sessions · single sign-on
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-start px-6 sm:px-16 py-20">
        <div className="w-full max-w-md">
          <p className="lg:hidden font-display text-xl tracking-[0.3em] text-gold mb-10">VINEFORD</p>
          <h2 className="font-display text-3xl text-[#F3F4F6]">Client & Staff Portal</h2>
          <p className="mt-3 text-sm text-[#9CA3AF]">Secure access to your investigation workspace.</p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs uppercase tracking-widest text-[#9CA3AF]">
                Email / username
              </Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="you@company.com"
                className="h-11 bg-white/5 border-white/15 focus-visible:ring-gold"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-[#9CA3AF]">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 bg-white/5 border-white/15 focus-visible:ring-gold"
                required
              />
            </div>

            {error && !offline ? (
              <p
                data-testid="login-error"
                className="rounded-md border-l-2 border-red-400 bg-red-400/10 px-3 py-2 text-sm text-red-300"
              >
                {error}
              </p>
            ) : null}

            {offline ? (
              <div
                data-testid="arkcase-connection-error"
                className="rounded-md border-l-4 border-gold bg-gold/10 px-4 py-4"
              >
                <p className="flex items-center gap-2 text-sm font-medium text-gold">
                  <WifiOff className="h-4 w-4" /> Secure connection unavailable
                </p>
                <p className="mt-2 text-sm leading-relaxed text-[#D1D5DB]">{error}</p>
              </div>
            ) : null}

            <Button
              data-testid="login-submit-btn"
              disabled={busy}
              className="h-11 w-full rounded-full bg-gold text-navy font-medium hover:bg-gold-dark transition-colors"
            >
              <Lock className="h-4 w-4 mr-2" /> {busy ? "Verifying…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-10 flex items-start gap-2 text-xs leading-relaxed text-[#6B7280]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#6B7280]" />
            Authorized access only. Contact the Vineford Intelligence support team if you need assistance with your
            account.
          </p>
        </div>
      </div>
    </div>
  );
}
